<section class="onboarding-section">
  <app-section-heading
    eyebrow="Booking"
    title="Book and Reserve Your Event"
    text="Select a preferred date and time, then provide your business, billing, and shipping details.">
  </app-section-heading>

  <form [formGroup]="form" (ngSubmit)="submit()" class="onboarding-form" novalidate>
    <div formGroupName="booking" class="onboarding-form__card">
      <h3>Select Date and Time</h3>
      <div class="onboarding-form__grid">
        <input type="hidden" formControlName="selectedDate" />

        <div class="onboarding-form__booking-layout">
          <div>
            <div class="max-w-md p-4 bg-white rounded-lg shadow border border-slate-200">
              <div class="flex items-center justify-between mb-4">
                <h4 class="text-lg font-semibold text-slate-800">{{ currentMonthLabel }}</h4>
                <div class="flex gap-2">
                  <button
                    type="button"
                    (click)="navigateMonth(-1)"
                    [disabled]="!canNavigateMonth(-1) || loadingCalendar"
                    class="calendar-nav-button"
                    aria-label="Previous month">
                    <span class="calendar-nav-icon" aria-hidden="true">◀</span>
                  </button>
                  <button
                    type="button"
                    (click)="navigateMonth(1)"
                    [disabled]="!canNavigateMonth(1) || loadingCalendar"
                    class="calendar-nav-button"
                    aria-label="Next month">
                    <span class="calendar-nav-icon" aria-hidden="true">▶</span>
                  </button>
                </div>
              </div>

              <div class="grid grid-cols-7 text-center text-sm font-medium text-slate-500 mb-2" role="row">
                @for (label of weekDayLabels; track label) {
                  <div role="columnheader">{{ label }}</div>
                }
              </div>

              <div class="grid grid-cols-7 gap-1" role="grid" aria-label="Booking date calendar">
                @for (week of calendarWeeks; track $index) {
                  @for (day of week; track day.isoDate) {
                    <button
                      type="button"
                      [attr.data-calendar-date]="day.isoDate"
                      [tabIndex]="getCalendarDayTabIndex(day)"
                      [attr.aria-selected]="day.isSelected"
                      [attr.aria-disabled]="day.isDisabled"
                      [attr.aria-label]="day.isoDate + ', ' + day.status"
                      [disabled]="day.isDisabled"
                      (click)="selectCalendarDay(day)"
                      (keydown)="onCalendarDayKeydown($event, day)"
                      [ngClass]="{
                        'text-slate-400 opacity-40': !day.isCurrentMonth,
                        'border border-slate-200 hover:border-blue-500 text-slate-700': day.status === 'available',
                        'bg-slate-200 text-slate-400 cursor-not-allowed': day.status === 'full',
                        'bg-red-100 text-red-700 border border-red-300 font-medium': day.status === 'pending',
                        'ring-2 ring-blue-500': day.isSelected
                      }"
                      class="h-10 w-10 mx-auto flex items-center justify-center rounded-md text-sm transition-colors">
                      {{ day.dayOfMonth }}
                    </button>
                  }
                }
              </div>
            </div>

            @if (loadingCalendar) {
              <span class="onboarding-form__hint">Loading calendar availability...</span>
            }

            @if (calendarMessage) {
              <span class="onboarding-form__hint onboarding-form__hint--error">{{ calendarMessage }}</span>
            }
          </div>

          <div>
            <label>
              <span>Time Slot</span>
              <select formControlName="selectedSlot">
                <option value="">Select a slot</option>
                @for (slot of slotOptions; track slot.startUtc) {
                  <option [value]="slot.startUtc">{{ formatSlotOptionLabel(slot) }}</option>
                }
              </select>

              @if (loadingAvailability) {
                <span class="onboarding-form__hint">Loading availability...</span>
              }

              @if (availabilityMessage) {
                <span class="onboarding-form__hint onboarding-form__hint--error">{{ availabilityMessage }}</span>
              }
            </label>

            @if (selectedDateDisplay || selectedSlotDisplay) {
              <p class="onboarding-form__hint">
                Selected booking:
                <strong>{{ selectedDateDisplay || 'Date not selected' }}</strong>
                @if (selectedSlotDisplay) {
                  <span> at <strong>{{ selectedSlotDisplay }}</strong></span>
                }
              </p>
            }
          </div>
        </div>
      </div>
    </div>

    <div formGroupName="company" class="onboarding-form__card">
      <h3>Company / Contact</h3>
      <div class="onboarding-form__grid two-col">
        <label><span>Contact Name</span><input type="text" formControlName="ContactName" /></label>
        <label><span>Contact Email</span><input type="email" formControlName="ContactEmail" /></label>
        <label><span>Display Name</span><input type="text" formControlName="DisplayName" /></label>
        <label><span>Legal Name</span><input type="text" formControlName="LegalName" /></label>
      </div>
    </div>

    <div formGroupName="billing" class="onboarding-form__card">
      <h3>Billing Information</h3>
      <div class="onboarding-form__grid two-col">
        <label><span>Business Name</span><input type="text" formControlName="BillingBusinessName" /></label>
        <label><span>Contact Name</span><input type="text" formControlName="BillingContactName" /></label>
        <label><span>Contact Email</span><input type="email" formControlName="BillingContactEmail" /></label>
        <label><span>Contact Phone</span><input type="tel" formControlName="BillingContactPhone" /></label>
        <label><span>Address 1</span><input type="text" formControlName="BillingAddress1" /></label>
        <label><span>Address 2</span><input type="text" formControlName="BillingAddress2" /></label>
        <label><span>City</span><input type="text" formControlName="BillingCity" /></label>
        <label><span>State</span><input type="text" formControlName="BillingState" /></label>
        <label><span>Zip Code</span><input type="text" formControlName="BillingZipCode" /></label>
        <label><span>Country</span><input type="text" formControlName="BillingCountry" /></label>
      </div>
    </div>

    <div formGroupName="shipping" class="onboarding-form__card">
      <h3>Shipping Information</h3>

      <label class="onboarding-form__checkbox">
        <input type="checkbox" formControlName="sameAsBilling" (change)="onCopyShippingFromBilling()" />
        <span>Same as billing</span>
      </label>

      <div class="onboarding-form__grid two-col"
           [class.onboarding-form__grid--readonly]="form.controls.shipping.controls.sameAsBilling.value">
        <label><span>Business Name</span><input type="text" formControlName="ShippingBusinessName" [readOnly]="form.controls.shipping.controls.sameAsBilling.value" /></label>
        <label><span>Contact Name</span><input type="text" formControlName="ShippingContactName" [readOnly]="form.controls.shipping.controls.sameAsBilling.value" /></label>
        <label><span>Contact Email</span><input type="email" formControlName="ShippingContactEmail" [readOnly]="form.controls.shipping.controls.sameAsBilling.value" /></label>
        <label><span>Contact Phone</span><input type="tel" formControlName="ShippingContactPhone" [readOnly]="form.controls.shipping.controls.sameAsBilling.value" /></label>
        <label><span>Address 1</span><input type="text" formControlName="ShippingAddress1" [readOnly]="form.controls.shipping.controls.sameAsBilling.value" /></label>
        <label><span>Address 2</span><input type="text" formControlName="ShippingAddress2" [readOnly]="form.controls.shipping.controls.sameAsBilling.value" /></label>
        <label><span>City</span><input type="text" formControlName="ShippingCity" [readOnly]="form.controls.shipping.controls.sameAsBilling.value" /></label>
        <label><span>State</span><input type="text" formControlName="ShippingState" [readOnly]="form.controls.shipping.controls.sameAsBilling.value" /></label>
        <label><span>Zip Code</span><input type="text" formControlName="ShippingZipCode" [readOnly]="form.controls.shipping.controls.sameAsBilling.value" /></label>
        <label><span>Country</span><input type="text" formControlName="ShippingCountry" [readOnly]="form.controls.shipping.controls.sameAsBilling.value" /></label>
      </div>
    </div>

    <input type="text" formControlName="honeypot" class="honeypot" autocomplete="off" tabindex="-1" />

    @if (errorMessage) {
      <p class="onboarding-form__error">{{ errorMessage }}</p>
    }

    @if (successMessage) {
      <p class="onboarding-form__success">{{ successMessage }}</p>
    }

    <button type="submit" [disabled]="submitting" class="onboarding-form__submit">
      {{ submitting ? 'Submitting...' : 'Request Booking' }}
    </button>
  </form>
</section>
